import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { pounds } from '../market-guides.service';
import { marketGuidesEnabled } from '../../site-config';
import { ANSWERS, BENEFICIARIES, COLLECTIONS, TRANSFERS } from './woyp-content';

/**
 * /whats-on-your-plate: how PlateX supports mental health and suicide
 * prevention charities. On www from launch.
 * Charities and figures come from woyp-content.ts; the reporting figures
 * appear only once real entries exist, and funds collected and funds donated
 * are always reported separately.
 */
@Component({
  selector: 'pla-whats-on-your-plate',
  templateUrl: './whats-on-your-plate.component.html',
  styleUrls: ['./whats-on-your-plate.component.scss'],
})
export class WhatsOnYourPlateComponent implements OnInit, OnDestroy {
  /** The answer plate in the hero: '' is the pause while the question waits. */
  public answer = '';
  private step = 0;
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(private zone: NgZone) {}

  ngOnInit(): void {
    const still = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (still) return;
    this.timer = setInterval(() => this.zone.run(() => this.next()), 2200);
  }

  ngOnDestroy(): void {
    if (this.timer) clearInterval(this.timer);
  }

  /** Blank (waiting), then each answer in turn, then back to waiting. */
  private next(): void {
    this.step = (this.step + 1) % (ANSWERS.length + 1);
    this.answer = this.step === 0 ? '' : ANSWERS[this.step - 1];
  }

  public readonly beneficiaries = BENEFICIARIES;
  public readonly collections = COLLECTIONS;
  public readonly transfers = [...TRANSFERS].sort((a, b) => b.date.localeCompare(a.date));
  public readonly money = pounds;
  /** The Market Guides link shows only where the guides are on (not www yet). */
  public readonly guides = marketGuidesEnabled;

  get collectedTotal(): number {
    return this.collections.reduce((s, c) => s + c.amountMinor, 0);
  }

  get donatedTotal(): number {
    return this.transfers.reduce((s, t) => s + t.amountMinor, 0);
  }

  /** Donated so far, per beneficiary. */
  get byBeneficiary(): { name: string; amountMinor: number }[] {
    const m = new Map<string, number>();
    this.transfers.forEach((t) => m.set(t.beneficiary, (m.get(t.beneficiary) || 0) + t.amountMinor));
    return Array.from(m, ([name, amountMinor]) => ({ name, amountMinor }));
  }

  /** Collected so far, per publication. */
  get byPublication(): { publication: string; amountMinor: number }[] {
    const m = new Map<string, number>();
    this.collections.forEach((c) => m.set(c.publication, (m.get(c.publication) || 0) + c.amountMinor));
    return Array.from(m, ([publication, amountMinor]) => ({ publication, amountMinor }));
  }

  get hasFigures(): boolean {
    return this.collections.length > 0 || this.transfers.length > 0;
  }

  day(iso: string): string {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  }
}
